import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Brain, ChevronDown, Sparkles } from 'lucide-react';
import type { ActividadHistorial } from '../../types/dashboard';
import { etiquetaDia } from '../../types/dashboard';

/**
 * Historial de actividad (P7 del plan de cierre).
 *
 * LO QUE ESTE COMPONENTE PROMETE Y LO QUE NO
 * -----------------------------------------
 * Promete **registrar**, no push ni medir. Es deliberado, y son tres reglas de
 * la Fase 4 escritas en el codigo que las cumple:
 *
 * 1. **Opt-in.** Se muestra solo si `gamificacionVisible` esta activo, igual que
 *    la barra de nivel. Quien no quiere gamificacion no ve ninguna de las dos.
 * 2. **Sin contadores.** No hay "12 actividades este mes". Una entrada por dia,
 *    y si un dia tuvo tres ejercicios se muestra UNO. Un total invariado es un
 *    recordatorio constante de lo que no se ha hecho.
 * 3. **Sin castigo por el hueco.** Si hoy no hay nada, el texto no dice "no has
 *    hecho nada hoy": dice que las actividades aparecerán aquí. La ausencia no se
 *    señala, se explica la funcion.
 *
 * Ademas: el historial es un `<ol>` con fecha como encabezado de grupo, para que
 * un lector de pantalla sepa cuando ocurrio cada cosa. Y cada fila es un boton
 * real de 48px que navega al modulo: un historial que no lleva a ninguna parte es
 * un archivo muerto.
 *
 * DESPLEGABLE, Y REPLEGADO DE ENTRADA
 * -----------------------------------
 * Arriba del todo solo haycia el heroe, el saludo y tres modulos; el historial
 * era lo unico que empujaba el resto hacia abajo y habia que hacer scroll para
 * llegar a los accesos. Plegado, la pantalla de entrada es la de las decisiones
 * (que hago hoy), no la del archivo del pasado.
 *
 * Dentro entran las CIFRAS y la lista, en ese orden: las dos son "lo que has
 * hecho". Se pasan desde la pagina (`resumen`) en vez de calcularlas aqui porque
 * quien tiene las estadisticas es el hook del dashboard; el panel solo decide
 * donde se ven y como se pliegan.
 *
 * El encabezado COMPLETO es el boton, con `aria-expanded` y `aria-controls`: con
 * un boton pequeno pegado al texto, el objetivo tactil en movil se queda en la
 * mitad del ancho de la tarjeta. El "Desplegar"/"Plegar" y el chevron van
 * `aria-hidden` porque el estado ya lo anuncia `aria-expanded`; si ademas fueran
 * texto, el nombre accesible de la region pasaria a ser "Lo que has hecho
 * Desplegar".
 */

const cardBase = 'rounded-card border border-v-outline bg-v-surface p-4 shadow-[0_1px_3px_rgb(0_0_0/0.05)]';

const ICONOS = {
  ejercicios: Sparkles,
  evaluacion: Brain,
} as const;

const RUTAS = {
  ejercicios: '/ejercicios',
  evaluacion: '/evaluacion',
} as const;

/** Cuantas entradas se muestran como maximo antes de resumir. */
const MAX_VISIBLES = 7;

export function HistorialActividad({
  actividades,
  resumen,
}: {
  actividades: readonly ActividadHistorial[];
  /** Las cifras de uso, que la pagina arma con las estadísticas. */
  resumen?: ReactNode;
}) {
  const navigate = useNavigate();
  /** Replegado de entrada: el dashboard abre con lo que hay que hacer hoy. */
  const [abierto, setAbierto] = useState(false);

  const visibles = actividades.slice(0, MAX_VISIBLES);
  const ocultas = actividades.length - visibles.length;

  return (
    <section aria-labelledby="titulo-historial" className={cardBase}>
      <h2 id="titulo-historial">
        <button
          type="button"
          onClick={() => setAbierto((v) => !v)}
          aria-expanded={abierto}
          aria-controls="lista-historial"
          className="flex min-h-12 w-full items-center gap-2 rounded-xl px-2 text-left transition-colors hover:bg-v-background focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-v-primary"
        >
          <ChevronDown
            aria-hidden
            className={`h-5 w-5 shrink-0 text-v-primary transition-transform duration-200 ${abierto ? 'rotate-180' : ''}`}
          />
          <span className="text-sm font-semibold text-v-on-surface">Lo que has hecho</span>
          {/* La etiqueta visible es lo que hace que se note que esto se abre. */}
          <span
            aria-hidden
            className="ml-auto inline-flex items-center rounded-pill bg-v-mint-container px-3 py-1 text-xs font-semibold text-v-mint-text"
          >
            {abierto ? 'Plegar' : 'Desplegar'}
          </span>
        </button>
      </h2>

      {/*
        El contenido se queda montado y se oculta con `hidden`: `aria-controls`
        apunta a un id que existe siempre, y el salto al plegarse no es un CLS
        porque el hueco ya estaba reservado desde el esqueleto.
      */}
      <div id="lista-historial" hidden={!abierto}>
        {resumen && <div className="mt-1">{resumen}</div>}

        {actividades.length === 0 ? (
          /* Copy neutro a proposito: informa de que la funcion existe sin señalar
             al estudiante por tener el historial vacio. */
          <p className="mt-1.5 text-sm text-v-on-surface-variant">
            Cuando completes un ejercicio, hables con el asistente o hagas una
            evaluación, aparecerá aquí.
          </p>
        ) : (
          <>
            <ol className="mt-2 space-y-1">
              {visibles.map((a) => {
                const Icono = ICONOS[a.modulo];

                return (
                  <li key={a.id}>
                    <button
                      type="button"
                      onClick={() => navigate(RUTAS[a.modulo])}
                      className="flex min-h-12 w-full items-center gap-3 rounded-xl px-2 text-left transition-colors hover:bg-v-background focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-v-primary"
                    >
                      <span
                        aria-hidden
                        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-v-mint-container text-v-mint-text"
                      >
                        <Icono className="h-4 w-4" />
                      </span>

                      {/* El dia va DENTRO del boton, no como columna aparte: asi el
                          objetivo tactil cubre la fila entera, que es lo que exige el
                          minimo de 48px. */}
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm text-v-on-surface">
                          {a.etiqueta}
                        </span>
                        <span className="block text-xs text-v-on-surface-variant">
                          {etiquetaDia(a.dia)}
                        </span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ol>

            {ocultas > 0 && (
              <p className="mt-2 px-2 text-xs text-v-on-surface-variant">
                Hay {ocultas} más en semanas anteriores.
              </p>
            )}
          </>
        )}
      </div>
    </section>
  );
}

/**
 * Estado de carga del historial.
 *
 * No es un spinner: es un hueco con la misma forma que la lista. Poner un
 * spinner aqui haria que el dashboard parpadeara al montar, porque el historial
 * llega después de las estadísticas.
 *
 * Reproduce la forma del panel **replegado** (como aparece al entrar): si
 * dibujara las filas de la lista, el salto al plegarse al cargar los datos sería
 * un CLS de manual.
 */
export function HistorialSkeleton() {
  return (
    <div className={cardBase} aria-busy="true">
      <div className="flex min-h-12 items-center gap-2 px-2">
        <div className="h-5 w-5 animate-pulse rounded bg-v-outline" />
        <div className="h-4 w-32 animate-pulse rounded bg-v-outline" />
        <div className="ml-auto h-6 w-20 animate-pulse rounded-pill bg-v-outline" />
      </div>
    </div>
  );
}